import { LocalEmployeeRepository } from '@/modules/employees/infrastructure/repositories/LocalEmployeeRepository';
import type { BiometricDataDTO } from '@/modules/employees/infrastructure/dto/EmployeeDTO';
import { TEMPLATE_CARD, TEMPLATE_PIN } from '@/modules/employees/domain/biometricConstants';
import { enqueueSyncOperation } from '@/shared/sync/syncQueue';

export type CardEnrollBiometric = { templateType: 'card'; decimal: number };
export type PinEnrollBiometric  = { templateType: 'pin';  pin: string };

export interface EnrollBiometricLocallyInput {
  idPerson: number;
  idEntity: number;
  biometric: CardEnrollBiometric | PinEnrollBiometric;
}

/**
 * Offline-first enrollment use case.
 *
 * Builds a BiometricDataDTO placeholder, persists it locally via
 * LocalEmployeeRepository.patchBiometricData, and enqueues a SyncQueueItem so
 * the sync worker can POST it to the remote API when connectivity returns.
 *
 * Both steps are wrapped in a single try/catch — if the local write fails, the
 * error is swallowed silently (the credential will be retried on next sync).
 *
 * Idempotency: enqueueSyncOperation is called with a stable id, so repeated
 * offline attempts for the same enrollment produce exactly one queue row.
 */
export async function enrollBiometricLocally(input: EnrollBiometricLocallyInput): Promise<void> {
  const { idPerson, idEntity, biometric } = input;
  const repo = new LocalEmployeeRepository();

  try {
    if (biometric.templateType === 'card') {
      const { decimal } = biometric;
      const entry: BiometricDataDTO = {
        nro_biometric: 1, // overwritten by patchBiometricData (existing.length + 1)
        biometric_data: {
          decimal: String(decimal),
          card_number: decimal,
          decimal_short: decimal,
          format_card: '',
          facility_code: 0,
        },
        id_template_biometric: TEMPLATE_CARD,
        metadata: [],
        id_entity_biometric: undefined,
        id_entity: idEntity,
        at_created: new Date().toISOString(),
      };

      await repo.patchBiometricData(idPerson, entry);
      await enqueueSyncOperation({
        id: `enroll-card-${idPerson}-${decimal}`,
        module: 'card_enrollment',
        operation: 'create',
        endpoint: `/v1/clock/app/person/${idEntity}/enroll/card`,
        payload: { decimal },
      });
    } else {
      const { pin } = biometric;
      const entry: BiometricDataDTO = {
        nro_biometric: 1, // overwritten by patchBiometricData
        biometric_data: [pin], // PIN shape: single string in array e.g. ["1212"]
        id_template_biometric: TEMPLATE_PIN,
        metadata: [],
        id_entity_biometric: undefined,
        id_entity: idEntity,
        at_created: new Date().toISOString(),
      };

      await repo.patchBiometricData(idPerson, entry);
      await enqueueSyncOperation({
        id: `enroll-pin-${idPerson}`,
        module: 'pin_enrollment',
        operation: 'create',
        endpoint: `/v1/clock/app/entity/${idEntity}/enroll/pin`,
        payload: { pin },
      });
    }
  } catch {
    // Silently swallow errors — the credential will be visible after the next
    // employee list sync from the backend.
  }
}
